import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../../components/Navbar';
import axiosInstance from '../../services/axiosInstance';
import useAdmin from '../../utile/useAdmin';

function AdaptiveCHRPage() {
  const { establishmentId } = useParams(); // Pour les routes /admin/:establishmentId
  const { isAdmin } = useAdmin();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [establishments, setEstablishments] = useState([]);
  const [currentEstablishment, setCurrentEstablishment] = useState(null);
  const [loading, setLoading] = useState(true);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  useEffect(() => {
    const fetchEstablishments = async () => {
      try {
        setLoading(true);
        
        if (establishmentId) {
          // Cas spécifique : affichage d'un établissement particulier
          if (isAdmin) {
            // Admin peut voir n'importe quel établissement
            const response = await axiosInstance.get(`/admin/establishments`);
            const establishment = response.data.establishments.find(est => est.id == establishmentId);
            if (establishment) {
              setCurrentEstablishment(establishment);
            } else {
              navigate('/404'); // Établissement non trouvé
            }
          } else {
            // Utilisateur normal : vérifier qu'il a accès à cet établissement
            const partnerUserId = localStorage.getItem('user_id');
            const response = await axiosInstance.get(`/partner_users/${partnerUserId}/get_establishments`);
            const userEstablishments = response.data.establishment || [];
            const establishment = userEstablishments.find(est => est.id == establishmentId);
            
            if (establishment) {
              setCurrentEstablishment(establishment);
            } else {
              // L'utilisateur n'a pas accès à cet établissement
              navigate('/chr'); // Rediriger vers ses établissements
            }
          }
        } else {
          // Cas général : affichage de tous les établissements accessibles
          if (isAdmin) {
            // Admin voit tous les établissements
            const response = await axiosInstance.get('/admin/establishments');
            setEstablishments(response.data.establishments);
          } else {
            // Utilisateur normal voit ses établissements
            const partnerUserId = localStorage.getItem('user_id');
            const response = await axiosInstance.get(`/partner_users/${partnerUserId}/get_establishments`);
            setEstablishments(response.data.establishment || []);
          }
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des établissements', error);
        if (error.response?.status === 403) {
          navigate('/accueil'); // Accès refusé
        }
      } finally {
        setLoading(false);
      }
    };

    fetchEstablishments();
  }, [establishmentId, isAdmin, navigate]);

  const handleAddEstablishment = async (newEstablishment) => {
    if (!isAdmin) {
      alert('Seuls les administrateurs peuvent ajouter des établissements');
      return null;
    }

    try {
      const response = await axiosInstance.post('/partner_users/add_establishment', newEstablishment);
      
      if (!establishmentId) {
        setEstablishments([...establishments, response.data.establishment]);
      }
      
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'ajout d\'un établissement', error);
      return null;
    }
  };

  const styles = {
    container: {
      backgroundColor: '#f8fafc',
      minHeight: '100vh',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    },
    header: {
      backgroundColor: '#ffffff',
      padding: '32px',
      borderBottom: '1px solid #e2e8f0',
      marginBottom: '32px'
    },
    headerContent: {
      maxWidth: '1200px',
      margin: '0 auto',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    title: {
      fontSize: '32px',
      fontWeight: '700',
      color: '#1e293b',
      margin: 0
    },
    subtitle: {
      fontSize: '16px',
      color: '#64748b',
      margin: '8px 0 0 0'
    },
    breadcrumb: {
      fontSize: '14px',
      color: '#64748b',
      marginBottom: '8px'
    },
    breadcrumbLink: {
      color: '#059669',
      textDecoration: 'none'
    },
    addButton: {
      backgroundColor: '#059669',
      color: '#ffffff',
      border: 'none',
      borderRadius: '12px',
      padding: '16px 24px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.2s ease-in-out',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
    },
    content: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 32px'
    },
    singleEstablishmentCard: {
      backgroundColor: '#ffffff',
      borderRadius: '16px',
      padding: '32px',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0'
    },
    establishmentHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '24px'
    },
    establishmentInfo: {
      flex: 1
    },
    establishmentName: {
      fontSize: '28px',
      fontWeight: '700',
      color: '#1e293b',
      marginBottom: '8px'
    },
    establishmentDetails: {
      fontSize: '16px',
      color: '#64748b',
      marginBottom: '4px'
    },
    statusBadge: {
      display: 'inline-block',
      padding: '6px 12px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '500',
      marginTop: '12px'
    },
    visibleBadge: {
      backgroundColor: '#dcfce7',
      color: '#166534'
    },
    hiddenBadge: {
      backgroundColor: '#fef2f2',
      color: '#dc2626'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
      gap: '24px',
      marginTop: '32px'
    },
    card: {
      backgroundColor: '#ffffff',
      borderRadius: '16px',
      padding: '24px',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0',
      transition: 'all 0.2s ease-in-out',
      cursor: 'pointer'
    },
    cardHover: {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
    },
    cardEstablishmentName: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#1e293b',
      marginBottom: '8px'
    },
    cardEstablishmentCity: {
      fontSize: '14px',
      color: '#64748b',
      marginBottom: '16px'
    },
    manageButton: {
      backgroundColor: '#f1f5f9',
      color: '#475569',
      border: 'none',
      borderRadius: '8px',
      padding: '12px 20px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease-in-out',
      textDecoration: 'none',
      display: 'inline-block'
    },
    emptyState: {
      textAlign: 'center',
      padding: '80px 32px',
      color: '#64748b'
    },
    emptyIcon: {
      fontSize: '64px',
      marginBottom: '16px'
    },
    emptyTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#374151',
      marginBottom: '8px'
    },
    emptyDescription: {
      fontSize: '16px',
      color: '#6b7280',
      marginBottom: '24px'
    },
    actionButtons: {
      display: 'flex',
      gap: '12px',
      marginTop: '24px'
    },
    secondaryButton: {
      backgroundColor: '#f1f5f9',
      color: '#475569',
      border: 'none',
      borderRadius: '8px',
      padding: '12px 20px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease-in-out'
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <Navbar />
        <div style={{ ...styles.content, textAlign: 'center', paddingTop: '100px' }}>
          <div style={{ fontSize: '18px', color: '#64748b' }}>Chargement...</div>
        </div>
      </div>
    );
  }

  // Affichage d'un établissement spécifique
  if (currentEstablishment) {
    return (
      <div style={styles.container}>
        <Navbar />
        
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <div>
              <div style={styles.breadcrumb}>
                <Link to="/accueil" style={styles.breadcrumbLink}>Accueil</Link>
                {' > '}
                <Link to="/chr" style={styles.breadcrumbLink}>Établissements</Link>
                {' > '}
                {currentEstablishment.name}
              </div>
              <h1 style={styles.title}>Gestion de l'établissement</h1>
              <p style={styles.subtitle}>
                Configurez et gérez votre établissement
              </p>
            </div>
          </div>
        </div>

        <div style={styles.content}>
          <div style={styles.singleEstablishmentCard}>
            <div style={styles.establishmentHeader}>
              <div style={styles.establishmentInfo}>
                <h2 style={styles.establishmentName}>{currentEstablishment.name}</h2>
                <div style={styles.establishmentDetails}>📍 {currentEstablishment.city}</div>
                <div style={styles.establishmentDetails}>📧 {currentEstablishment.email}</div>
                <div style={styles.establishmentDetails}>📞 {currentEstablishment.phone}</div>
                <span style={{
                  ...styles.statusBadge,
                  ...(currentEstablishment.visible ? styles.visibleBadge : styles.hiddenBadge)
                }}>
                  {currentEstablishment.visible ? 'Visible' : 'Masqué'}
                </span>
              </div>
            </div>

            <div style={styles.actionButtons}>
              <button style={styles.manageButton}>
                📸 Gérer les photos
              </button>
              <button style={styles.manageButton}>
                🍽️ Gérer le menu
              </button>
              <button style={styles.manageButton}>
                ⏰ Gérer les horaires
              </button>
              <button style={styles.manageButton}>
                ⚙️ Paramètres
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Affichage de la liste des établissements
  return (
    <div style={styles.container}>
      <Navbar />
      
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div>
            <h1 style={styles.title}>
              {isAdmin ? 'Tous les Établissements' : 'Mes Établissements'}
            </h1>
            <p style={styles.subtitle}>
              {isAdmin 
                ? 'Gérez tous les établissements de la plateforme'
                : 'Gérez vos établissements et leurs informations'
              }
            </p>
          </div>
          {isAdmin && (
            <button
              onClick={handleOpen}
              style={styles.addButton}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#047857';
                e.target.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#059669';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              <span style={{ fontSize: '20px' }}>+</span>
              Ajouter un établissement
            </button>
          )}
        </div>
      </div>

      <div style={styles.content}>
        {establishments.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>🏪</div>
            <h3 style={styles.emptyTitle}>
              {isAdmin ? 'Aucun établissement trouvé' : 'Aucun établissement assigné'}
            </h3>
            <p style={styles.emptyDescription}>
              {isAdmin 
                ? 'Commencez par ajouter le premier établissement de la plateforme.'
                : 'Vous n\'êtes associé à aucun établissement. Contactez votre administrateur pour obtenir l\'accès.'
              }
            </p>
            {isAdmin && (
              <button
                onClick={handleOpen}
                style={{
                  ...styles.addButton,
                  margin: '0 auto'
                }}
              >
                <span style={{ fontSize: '20px' }}>+</span>
                Ajouter le premier établissement
              </button>
            )}
          </div>
        ) : (
          <div style={styles.grid}>
            {establishments.map((establishment, index) => (
              <div
                key={establishment.id || index}
                style={styles.card}
                onMouseEnter={(e) => {
                  Object.assign(e.currentTarget.style, styles.cardHover);
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1)';
                }}
              >
                <h3 style={styles.cardEstablishmentName}>
                  {establishment.name}
                </h3>
                <p style={styles.cardEstablishmentCity}>
                  📍 {establishment.city}
                </p>
                {isAdmin && establishment.partners && (
                  <p style={styles.cardEstablishmentCity}>
                    👥 {establishment.partners.length} partenaire(s)
                  </p>
                )}
                <span style={{
                  ...styles.statusBadge,
                  ...(establishment.visible ? styles.visibleBadge : styles.hiddenBadge)
                }}>
                  {establishment.visible ? 'Visible' : 'Masqué'}
                </span>
                <br />
                <Link
                  to={`/admin/${establishment.id}`}
                  style={styles.manageButton}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#e2e8f0';
                    e.target.style.color = '#334155';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#f1f5f9';
                    e.target.style.color = '#475569';
                  }}
                >
                  Gérer l'établissement →
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>

      {isAdmin && (
        <EstablishmentModal 
          open={open} 
          onClose={handleClose} 
          onAdd={handleAddEstablishment} 
        />
      )}
    </div>
  );
}

// Composant Modal inchangé mais conditionnel à isAdmin
function EstablishmentModal({ open, onClose, onAdd }) {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const getCoordinates = async (address) => {
    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${address}`;
    const response = await axios.get(url);
    if (response.data && response.data[0]) {
      return {
        lat: parseFloat(response.data[0].lat),
        lon: parseFloat(response.data[0].lon)
      };
    }
    return null;
  };

  const handleAdd = async () => {
    if (!name || !address || !email || !phone) {
      alert('Veuillez remplir tous les champs obligatoires.');
      return;
    }

    setIsLoading(true);
    
    try {
      const coordinates = await getCoordinates(address);
      if (!coordinates) {
        alert("Impossible d'obtenir les coordonnées pour cette adresse.");
        setIsLoading(false);
        return;
      }

      const newEstablishment = {
        partner_user_id: localStorage.getItem('user_id'),
        name,
        address,
        lat: coordinates.lat,
        lon: coordinates.lon,
        email,
        phone,
      };

      const response = await onAdd(newEstablishment);
      setName('');
      setAddress('');
      setEmail('');
      setPhone('');
      onClose();

      if (response && response.account_link) {
        window.location.href = response.account_link;
      }
    } catch (error) {
      console.error('Erreur lors de l\'ajout:', error);
      alert('Une erreur est survenue lors de l\'ajout de l\'établissement.');
    } finally {
      setIsLoading(false);
    }
  };

  const modalStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: open ? 'flex' : 'none',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    modal: {
      backgroundColor: '#ffffff',
      borderRadius: '16px',
      padding: '32px',
      width: '100%',
      maxWidth: '500px',
      maxHeight: '90vh',
      overflow: 'auto',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
    },
    title: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#1e293b',
      marginBottom: '24px',
      textAlign: 'center'
    },
    inputGroup: {
      marginBottom: '20px'
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '8px'
    },
    input: {
      width: '100%',
      padding: '12px 16px',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      fontSize: '16px',
      transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
      outline: 'none',
      backgroundColor: '#ffffff',
      boxSizing: 'border-box'
    },
    buttonGroup: {
      display: 'flex',
      gap: '12px',
      marginTop: '32px'
    },
    button: {
      flex: 1,
      padding: '12px 24px',
      border: 'none',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s ease-in-out'
    },
    primaryButton: {
      backgroundColor: '#059669',
      color: '#ffffff'
    },
    secondaryButton: {
      backgroundColor: '#f1f5f9',
      color: '#475569'
    }
  };

  if (!open) return null;

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.modal} onClick={(e) => e.stopPropagation()}>
        <h2 style={modalStyles.title}>Ajouter un nouvel établissement</h2>
        
        <div style={modalStyles.inputGroup}>
          <label style={modalStyles.label}>Nom de l'établissement *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={modalStyles.input}
            placeholder="Restaurant Le Bon Goût"
            onFocus={(e) => {
              e.target.style.borderColor = '#059669';
              e.target.style.boxShadow = '0 0 0 3px rgba(5, 150, 105, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#d1d5db';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>

        <div style={modalStyles.inputGroup}>
          <label style={modalStyles.label}>Ville *</label>
          <input
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            style={modalStyles.input}
            placeholder="Paris, France"
            onFocus={(e) => {
              e.target.style.borderColor = '#059669';
              e.target.style.boxShadow = '0 0 0 3px rgba(5, 150, 105, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#d1d5db';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>

        <div style={modalStyles.inputGroup}>
          <label style={modalStyles.label}>Email *</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={modalStyles.input}
            placeholder="contact@restaurant.com"
            onFocus={(e) => {
              e.target.style.borderColor = '#059669';
              e.target.style.boxShadow = '0 0 0 3px rgba(5, 150, 105, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#d1d5db';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>

        <div style={modalStyles.inputGroup}>
          <label style={modalStyles.label}>Téléphone *</label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            style={modalStyles.input}
            placeholder="01 23 45 67 89"
            onFocus={(e) => {
              e.target.style.borderColor = '#059669';
              e.target.style.boxShadow = '0 0 0 3px rgba(5, 150, 105, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#d1d5db';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>

        <div style={modalStyles.buttonGroup}>
          <button
            type="button"
            onClick={onClose}
            style={{
              ...modalStyles.button,
              ...modalStyles.secondaryButton
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#e2e8f0';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#f1f5f9';
            }}
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleAdd}
            disabled={isLoading}
            style={{
              ...modalStyles.button,
              ...modalStyles.primaryButton,
              backgroundColor: isLoading ? '#9ca3af' : '#059669',
              cursor: isLoading ? 'not-allowed' : 'pointer'
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                e.target.style.backgroundColor = '#047857';
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading) {
                e.target.style.backgroundColor = '#059669';
              }
            }}
          >
            {isLoading ? 'Ajout en cours...' : 'Ajouter l\'établissement'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdaptiveCHRPage;